import { useCallback, useMemo, useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import type { CatalogData } from '@/types';
import {
  groupModelsByStop,
  findStopForModel,
  getDefaultModel,
  MODEL_SIZE_STOPS
} from '@/lib/modelSizeSlider';
import { cn } from '@/lib/utils';

type ModelSizeSliderProps = {
  catalog?: CatalogData | null;
  selectedModelId: string | null;
  onChange: (modelId: string) => void;
  disabled?: boolean;
};

export const ModelSizeSlider = ({
  catalog,
  selectedModelId,
  onChange,
  disabled = false
}: ModelSizeSliderProps) => {
  // Guard: Show loading state when catalog is not ready
  if (!catalog || !catalog.models || catalog.models.length === 0) {
    return (
      <div className="space-y-3">
        <div className="h-10 animate-pulse rounded-md bg-muted" />
        <p className="text-sm text-muted-foreground">Loading models...</p>
      </div>
    );
  }

  const models = catalog.models;

  const { groupedModels, availableStopSet, firstAvailableIndex } = useMemo(() => {
    const grouped = groupModelsByStop(models);
    const available = new Set(Array.from(grouped.keys()));
    const firstIndex = MODEL_SIZE_STOPS.findIndex(stop => available.has(stop.value));

    return {
      groupedModels: grouped,
      availableStopSet: available,
      firstAvailableIndex: firstIndex >= 0 ? firstIndex : 0,
    };
  }, [models]);

  // Determine current stop index based on selected model
  const initialStopIndex = useMemo(() => {
    if (!models || models.length === 0) {
      return 0;
    }

    if (selectedModelId) {
      const selectedModel = models.find(m => m.model_id === selectedModelId);
      if (selectedModel) {
        const stop = findStopForModel(selectedModel.params_b);
        if (stop) {
          const stopIdx = MODEL_SIZE_STOPS.findIndex(s => s.value === stop.value);
          if (stopIdx >= 0) {
            return stopIdx;
          }
        } else {
          console.warn('[ModelSizeSlider] No stop found for model params:', selectedModel.params_b);
        }
      } else {
        // Model not found - try to find a similar model and migrate
        const migratedModel = models.find(m => 
          selectedModelId.includes(m.family) || 
          m.model_id.includes(selectedModelId.split('-')[0])
        );
        
        if (migratedModel) {
          console.info(`[ModelSizeSlider] Migrating ${selectedModelId} to ${migratedModel.model_id}`);
          // Trigger migration to the new model ID
          setTimeout(() => onChange(migratedModel.model_id), 0);
          
          const stop = findStopForModel(migratedModel.params_b);
          if (stop) {
            const stopIdx = MODEL_SIZE_STOPS.findIndex(s => s.value === stop.value);
            if (stopIdx >= 0) {
              return stopIdx;
            }
          }
        } else {
          console.warn('[ModelSizeSlider] Selected model not found in catalog:', selectedModelId);
        }
      }
    }

    // Default to 70B stop (index 3 in MODEL_SIZE_STOPS array)
    const default70BIndex = MODEL_SIZE_STOPS.findIndex(s => s.value === 70);
    if (default70BIndex >= 0 && availableStopSet.has(70)) {
      return default70BIndex;
    }

    return firstAvailableIndex;
  }, [selectedModelId, models, firstAvailableIndex, availableStopSet, onChange]);

  const [stopIndex, setStopIndex] = useState(initialStopIndex);

  // Update stop index when selected model changes externally
  useEffect(() => {
    setStopIndex(initialStopIndex);
  }, [initialStopIndex]);

  const currentStop = MODEL_SIZE_STOPS[stopIndex] || MODEL_SIZE_STOPS[firstAvailableIndex] || MODEL_SIZE_STOPS[0];
  const currentModels = currentStop ? groupedModels.get(currentStop.value) || [] : [];

  const findNearestStopIndexWithModels = useCallback((index: number) => {
    if (index < 0 || index >= MODEL_SIZE_STOPS.length) {
      return null;
    }

    const stopAtIndex = MODEL_SIZE_STOPS[index];
    if (availableStopSet.has(stopAtIndex.value)) {
      return index;
    }

    for (let distance = 1; distance < MODEL_SIZE_STOPS.length; distance++) {
      const prev = index - distance;
      if (prev >= 0) {
        const prevStop = MODEL_SIZE_STOPS[prev];
        if (availableStopSet.has(prevStop.value)) {
          return prev;
        }
      }

      const next = index + distance;
      if (next < MODEL_SIZE_STOPS.length) {
        const nextStop = MODEL_SIZE_STOPS[next];
        if (availableStopSet.has(nextStop.value)) {
          return next;
        }
      }
    }

    return null;
  }, [availableStopSet]);

  // Ensure selected model is in current stop, otherwise select default
  useEffect(() => {
    // Guard: catalog not loaded
    if (!catalog || !catalog.models) return;
    
    // Guard: no stop or models
    if (!currentStop) return;

    if (!currentModels.length) {
      const fallbackIndex = findNearestStopIndexWithModels(stopIndex);
      if (fallbackIndex !== null && fallbackIndex !== stopIndex) {
        setStopIndex(fallbackIndex);
        const fallbackStop = MODEL_SIZE_STOPS[fallbackIndex];
        const fallbackModels = groupedModels.get(fallbackStop.value) || [];
        const defaultModel = fallbackModels[0];
        if (defaultModel) {
          onChange(defaultModel.model_id);
        }
      }
      return;
    }

    const isSelectedInCurrentStop = currentModels.some(m => m.model_id === selectedModelId);
    if (!isSelectedInCurrentStop) {
      const defaultModel = currentModels[0];
      if (defaultModel) {
        onChange(defaultModel.model_id);
      }
    }
  }, [
    catalog,
    currentStop,
    currentModels,
    selectedModelId,
    onChange,
    findNearestStopIndexWithModels,
    stopIndex,
    groupedModels,
  ]);

  // Initialize with default model when selectedModelId is null
  useEffect(() => {
    if (!catalog || !catalog.models || selectedModelId !== null) return;
    
    const defaultModel = getDefaultModel(catalog.models);
    if (defaultModel) {
      onChange(defaultModel.model_id);
    }
  }, [catalog, selectedModelId, onChange]);

  const handleSliderChange = (value: number[]) => {
    const requestedIndex = Math.round(value[0]);
    if (requestedIndex < 0 || requestedIndex >= MODEL_SIZE_STOPS.length) {
      return;
    }

    let targetIndex = requestedIndex;
    const requestedStop = MODEL_SIZE_STOPS[requestedIndex];
    if (!availableStopSet.has(requestedStop.value)) {
      const fallbackIndex = findNearestStopIndexWithModels(requestedIndex);
      if (fallbackIndex === null) {
        setStopIndex(requestedIndex);
        return;
      }
      targetIndex = fallbackIndex;
    }

    if (targetIndex !== stopIndex) {
      setStopIndex(targetIndex);
    }

    const targetStop = MODEL_SIZE_STOPS[targetIndex];
    const newStopModels = groupedModels.get(targetStop.value) || [];
    const defaultModel = newStopModels[0];

    if (defaultModel && defaultModel.model_id !== selectedModelId) {
      onChange(defaultModel.model_id);
    }
  };

  const handleBadgeClick = (modelId: string) => {
    onChange(modelId);
  };

  if ((!models || models.length === 0) || !currentStop) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">No models available</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Title with Tooltip */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs font-medium">Model</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-3 w-3 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">AI model size - larger models are more capable but require more resources</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Slider */}
      <div className="px-2">
        <Slider
          value={[stopIndex]}
          onValueChange={handleSliderChange}
          min={0}
          max={MODEL_SIZE_STOPS.length - 1}
          step={1}
          disabled={disabled}
        />
      </div>

      {/* Stop labels under slider */}
      <div className="flex justify-between px-2">
        {MODEL_SIZE_STOPS.map((stop, index) => {
          const isActive = stopIndex === index;
          const hasModels = availableStopSet.has(stop.value);
          return (
            <div key={stop.value} className="flex flex-col items-center">
              <span
                className={cn(
                  'text-xs transition-colors',
                  isActive && 'font-semibold text-foreground',
                  !isActive && hasModels && 'text-muted-foreground',
                  !isActive && !hasModels && 'text-muted-foreground/40'
                )}
              >
                {stop.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Model badges */}
      {currentModels.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {currentModels.map(model => {
            const isSelected = model.model_id === selectedModelId;
            const displayName = model.display_name || model.model_id;
            
            return (
              <Badge
                key={model.model_id}
                variant={isSelected ? "default" : "outline"}
                className={cn(
                  "cursor-pointer transition-all",
                  isSelected && 'ring-2 ring-primary ring-offset-1'
                )}
                onClick={() => handleBadgeClick(model.model_id)}
              >
                {displayName}
              </Badge>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center">
          No models available at this size
        </p>
      )}
    </div>
  );
};
